import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import {
  ChevronsDown,
  Crown,
  UserCheck,
  UserMinus,
} from "lucide-react";

import { Avatar } from "../../components/Avatar/Avatar";
import { Button } from "../../components/Button/Button";
import { Menu, type MenuItem, type MenuSeparator } from "../../components/Menu/Menu";
import { Modal } from "../../components/Modal/Modal";
import { useOptionalToast } from "../../components/Toast/Toast";
import { ApiError, describeError } from "../../lib/errors";
import { kickMember, updateMemberRole } from "./api";
import type { GuildDetail, GuildMember } from "./types";
import styles from "./GuildMembersTable.module.css";

interface GuildMembersTableProps {
  projectId: string;
  guildId: string;
  /** The loaded guild — supplies `members` and `name` (used in modal copy). */
  guild: GuildDetail;
  /** Called after any successful member mutation so the parent re-fetches. */
  onChanged: () => void;
}

/** A pending confirm-modal action — kick a member or transfer leadership. */
type PendingAction = { member: GuildMember; action: "kick" | "leader" };

const ROLE_LABEL: Record<GuildMember["role"], string> = {
  leader: "Leader",
  officer: "Officer",
  member: "Member",
};

/**
 * Verbose relative time — "5 weeks ago", "3 months ago". Mirrors the Joined
 * column of the Members table in docs/mockups/guild-detail.html.
 */
function formatRelativeLong(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return plural(min, "minute");
  const hr = Math.floor(min / 60);
  if (hr < 24) return plural(hr, "hour");
  const day = Math.floor(hr / 24);
  if (day < 7) return plural(day, "day");
  const week = Math.floor(day / 7);
  if (week < 5) return plural(week, "week");
  const month = Math.floor(day / 30);
  if (month < 12) return plural(month, "month");
  return plural(Math.floor(day / 365), "year");
}

function plural(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? "" : "s"} ago`;
}

/**
 * The Members section of `GuildDetailPage`. Renders the guild roster with a
 * role-contextual per-row action menu (mockup screens 1-2). Promote / Demote
 * apply directly; Kick and Make-leader route through a confirm modal (mockup
 * screens 3-4). After any successful mutation the parent re-fetches via
 * `onChanged`.
 */
export function GuildMembersTable({
  projectId,
  guildId,
  guild,
  onChanged,
}: GuildMembersTableProps) {
  const toast = useOptionalToast();
  const [pending, setPending] = useState<PendingAction | null>(null);
  // Error surfaced inside the open confirm modal.
  const [modalError, setModalError] = useState<string | null>(null);

  const currentLeader = guild.members.find((m) => m.role === "leader");

  /** Translate any thrown error into a human message. */
  function messageFor(err: unknown): string {
    return err instanceof ApiError
      ? describeError(err.code)
      : describeError("internal");
  }

  // Direct promote / demote — applies immediately, no modal.
  const roleMutation = useMutation({
    mutationFn: ({
      playerId,
      role,
    }: {
      playerId: string;
      role: "leader" | "officer" | "member";
    }) => updateMemberRole(projectId, guildId, playerId, role),
    onSuccess: () => onChanged(),
    onError: (err) => {
      toast?.push({ variant: "danger", message: messageFor(err) });
    },
  });

  // Confirm-modal mutation — kick OR transfer leadership.
  const confirmMutation = useMutation({
    mutationFn: (p: PendingAction) =>
      p.action === "kick"
        ? kickMember(projectId, guildId, p.member.player_id)
        : updateMemberRole(
            projectId,
            guildId,
            p.member.player_id,
            "leader",
          ).then(() => undefined),
    onSuccess: () => {
      setPending(null);
      setModalError(null);
      onChanged();
    },
    onError: (err) => {
      setModalError(messageFor(err));
    },
  });

  function closeModal() {
    if (confirmMutation.isPending) return;
    setPending(null);
    setModalError(null);
  }

  function menuItems(m: GuildMember): (MenuItem | MenuSeparator)[] {
    const items: (MenuItem | MenuSeparator)[] = [];
    if (m.role === "member") {
      items.push({
        label: "Promote to officer",
        icon: <UserCheck size={14} aria-hidden="true" />,
        onSelect: () =>
          roleMutation.mutate({ playerId: m.player_id, role: "officer" }),
      });
    } else {
      items.push({
        label: "Demote to member",
        icon: <ChevronsDown size={14} aria-hidden="true" />,
        onSelect: () =>
          roleMutation.mutate({ playerId: m.player_id, role: "member" }),
      });
    }
    items.push({
      label: "Make leader",
      icon: <Crown size={14} aria-hidden="true" />,
      onSelect: () => {
        setModalError(null);
        setPending({ member: m, action: "leader" });
      },
    });
    items.push({ separator: true });
    items.push({
      label: "Kick member",
      icon: <UserMinus size={14} aria-hidden="true" />,
      variant: "danger",
      onSelect: () => {
        setModalError(null);
        setPending({ member: m, action: "kick" });
      },
    });
    return items;
  }

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>Members</h3>
        <span className={styles.cardSub}>
          {guild.member_count} {guild.member_count === 1 ? "member" : "members"}
        </span>
      </div>

      <table className={styles.table}>
        <thead>
          <tr>
            <th style={{ width: "50%" }}>Member</th>
            <th>Role</th>
            <th>Joined</th>
            <th aria-hidden="true" />
          </tr>
        </thead>
        <tbody>
          {guild.members.map((m) => (
            <tr key={m.player_id}>
              <td>
                <div className={styles.memberCell}>
                  <Avatar
                    name={m.display_name}
                    image={m.avatar ?? undefined}
                    size={30}
                  />
                  <Link
                    to={`/projects/${projectId}/players/${m.player_id}`}
                    className={styles.link}
                  >
                    {m.display_name}
                  </Link>
                </div>
              </td>
              <td>
                <span className={`${styles.roleBadge} ${styles[m.role]}`}>
                  {ROLE_LABEL[m.role]}
                </span>
              </td>
              <td className={styles.muted}>
                {formatRelativeLong(m.joined_at)}
              </td>
              <td className={styles.act}>
                {m.role !== "leader" && (
                  <Menu
                    aria-label="Member actions"
                    items={menuItems(m)}
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {pending && pending.action === "kick" && (
        <KickModal
          member={pending.member}
          guild={guild}
          error={modalError}
          busy={confirmMutation.isPending}
          onClose={closeModal}
          onConfirm={() => confirmMutation.mutate(pending)}
        />
      )}
      {pending && pending.action === "leader" && (
        <MakeLeaderModal
          member={pending.member}
          currentLeaderName={currentLeader?.display_name ?? "the current leader"}
          error={modalError}
          busy={confirmMutation.isPending}
          onClose={closeModal}
          onConfirm={() => confirmMutation.mutate(pending)}
        />
      )}
    </div>
  );
}

/** Kick-member confirm modal — mockup screen 3. */
function KickModal({
  member,
  guild,
  error,
  busy,
  onClose,
  onConfirm,
}: {
  member: GuildMember;
  guild: GuildDetail;
  error: string | null;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const isOpen = guild.visibility === "open";
  return (
    <Modal
      open
      onClose={onClose}
      title={`Kick ${member.display_name}?`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={onConfirm}
            disabled={busy}
            loading={busy}
            icon={<UserMinus size={14} aria-hidden="true" />}
          >
            Kick member
          </Button>
        </>
      }
    >
      <p className={styles.modalBody}>
        <b>{member.display_name}</b> will be removed from{" "}
        <b>{guild.name}</b>. The guild is{" "}
        <b>{isOpen ? "Open" : "Invite only"}</b>, so they{" "}
        {isOpen
          ? "can rejoin at any time — set the guild to Invite only first if you want the removal to stick."
          : "will need a fresh invite to return."}
      </p>
      {error && (
        <p className={styles.modalBody} role="alert" style={{ marginTop: 10 }}>
          {error}
        </p>
      )}
    </Modal>
  );
}

/** Make-leader confirm modal — mockup screen 4. */
function MakeLeaderModal({
  member,
  currentLeaderName,
  error,
  busy,
  onClose,
  onConfirm,
}: {
  member: GuildMember;
  currentLeaderName: string;
  error: string | null;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      open
      onClose={onClose}
      title={`Make ${member.display_name} the leader?`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            disabled={busy}
            loading={busy}
            icon={<Crown size={14} aria-hidden="true" />}
          >
            Make leader
          </Button>
        </>
      }
    >
      <p className={styles.modalBody}>
        <b>{member.display_name}</b> becomes the guild leader.{" "}
        <b>{currentLeaderName}</b> — the current leader — is demoted to officer.
        Takes effect immediately.
      </p>
      {error && (
        <p className={styles.modalBody} role="alert" style={{ marginTop: 10 }}>
          {error}
        </p>
      )}
    </Modal>
  );
}
